import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2 } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { saveAdminAuth, getAdminToken } from "@/lib/adminAuth";

export default function AdminLoginPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (getAdminToken()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      saveAdminAuth(data);
      toast.success(`Welcome back, ${data.full_name || data.email}.`);
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-grey-light">
      <SiteHeader />
      <div className="max-w-md mx-auto px-5 py-20">
        <div className="border border-grey bg-white p-10">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-purple text-white mb-6">
            <Lock size={20} />
          </div>
          <div className="text-xs tracking-[0.22em] uppercase text-purple-light font-bold mb-3">
            Administrator sign-in
          </div>
          <h1 className="h-serif text-3xl text-purple-dark mb-6 tracking-tight">
            Zion Christian Academy admin
          </h1>
          <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                Email
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="border-grey focus:border-purple focus:ring-purple"
                data-testid="admin-email"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
                Password
              </Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-grey focus:border-purple focus:ring-purple"
                data-testid="admin-password"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-purple text-white hover:bg-purple-dark font-bold py-3 rounded-sm text-sm transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="admin-login-btn"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {submitting ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
